/**
 * Drawer Animation Utility
 * Provides reusable GSAP animation functionality for drawers and modals
 * Based on the ModalDialog implementation in global.js
 */

class DrawerAnimation {
  constructor(element, container, options = {}) {
    this.element = element;
    this.container = container || element.firstElementChild;
    this.options = Object.assign({
      type: 'drawer', // 'drawer' or 'modal'
      addOverlayToBody: false,
      onOpen: null,
      onClose: null,
      drawerPosition: 'right', // 'right' or 'left'
    }, options);

    this.timeline = null;
    this.overlay = null;

    if (this.options.addOverlayToBody) {
      this.overlay = this.createOverlay();
    }

    this.buildTimeline();
  }

  /**
   * Build the GSAP timeline for the animation
   */
  buildTimeline() {
    if (this.timeline) this.timeline.kill();

    this.timeline = gsap.timeline({
      paused: true,
      onStart: () => this.onStateChange(true),
      onReverseComplete: () => this.onStateChange(false),
    });

    if (this.options.type === 'modal') {
      this.timeline.from(this.container, {
        scale: 0.7,
        autoAlpha: 0,
        duration: 0.2,
        ease: 'power1.inOut',
      });
    } else {
      const fromVars = {
        duration: 0.5,
        ease: 'power2.inOut',
      };

      if (this.options.drawerPosition === 'left') {
        fromVars.xPercent = -100;
      } else {
        fromVars.xPercent = 100;
      }

      this.timeline.from(this.container, fromVars);
    }

    if (this.element.hasAttribute('open') || this.element.classList.contains('active')) {
      this.timeline.progress(1);
    }
  }

  /**
   * Create an overlay element
   * @returns {HTMLElement} The created overlay element
   */
  createOverlay() {
    const existingOverlay = document.querySelector('[data-drawer-overlay]');
    if (existingOverlay) return existingOverlay;

    const overlayElement = document.createElement('div');
    overlayElement.classList.add('drawer-overlay');
    overlayElement.dataset.drawerOverlay = true;
    document.body.appendChild(overlayElement);

    return overlayElement;
  }

  /**
   * Handle state changes (open/close)
   * @param {boolean} open - Whether the drawer is opening or closing
   */
  onStateChange(open = true) {
    document.body.classList.toggle('overflow-hidden', open);

    if (this.overlay) {
      document.body.classList.toggle(`has-open-${this.options.type}`, open);
    }

    this.element.toggleAttribute('open', open);
    this.element.classList.toggle('active', open);

    if (open && typeof this.options.onOpen === 'function') {
      this.options.onOpen();
    } else if (!open && typeof this.options.onClose === 'function') {
      this.options.onClose();
    }
  }

  /**
   * Animate the drawer open or closed
   * @param {boolean} open - Whether to open or close the drawer
   */
  animate(open = true) {
    if (open) {
      this.timeline?.play();
    } else {
      this.timeline?.reverse();
    }
  }

  /**
   * Open the drawer
   */
  open() {
    this.animate(true);
  }

  /**
   * Close the drawer
   */
  close() {
    this.animate(false);
  }

  /**
   * Check if the drawer is open
   * @returns {boolean} Whether the drawer is open
   */
  isOpen() {
    return this.element.hasAttribute('open') || this.element.classList.contains('active');
  }
}

window.DrawerAnimation = DrawerAnimation;